/**
 * Shard-aware reads/writes for team roster fields (legacy memberDetails vs memberDetailsShards).
 * Mirrors semantics in functions/teamMemberDetailsShared.js (user is never in both).
 */

const SHARD_LIST_KEY = 'memberDetailsShardIds';
const SHARDS_COLLECTION = 'memberDetailsShards';
const SHARD_MEMBERS_FIELD = 'members';

/**
 * @param {import('firebase').firestore.Transaction} transaction
 * @param {import('firebase').firestore.DocumentReference} teamRef
 * @param {Record<string, unknown>} teamDocData
 * @returns {Promise<Array<{ id: string, snap: import('firebase').firestore.DocumentSnapshot }>>}
 */
export async function readShardSnapshotsInTransaction(
  transaction,
  teamRef,
  teamDocData
) {
  const shardIds = Array.isArray(teamDocData[SHARD_LIST_KEY])
    ? teamDocData[SHARD_LIST_KEY]
    : [];
  const out = [];
  for (const sid of shardIds) {
    const sref = teamRef.collection(SHARDS_COLLECTION).doc(sid);
    out.push({ id: sid, snap: await transaction.get(sref) });
  }
  return out;
}

/**
 * @param {import('firebase').firestore.Firestore} db
 * @param {string} teamId
 * @returns {Promise<{
 *   teamRef: import('firebase').firestore.DocumentReference,
 *   teamSnap: import('firebase').firestore.DocumentSnapshot,
 *   shardSnapshots: Array<{ id: string, snap: import('firebase').firestore.DocumentSnapshot }>
 * }>}
 */
export async function loadTeamAndShardSnapshots(db, teamId) {
  const teamRef = db.collection('teamDatabase').doc(teamId);
  const teamSnap = await teamRef.get();
  if (!teamSnap.exists) {
    return { teamRef, teamSnap, shardSnapshots: [] };
  }
  const data = teamSnap.data() || {};
  const shardIds = Array.isArray(data[SHARD_LIST_KEY])
    ? data[SHARD_LIST_KEY]
    : [];
  const shardSnapshots = await Promise.all(
    shardIds.map(async (id) => ({
      id,
      snap: await teamRef.collection(SHARDS_COLLECTION).doc(id).get(),
    }))
  );
  return { teamRef, teamSnap, shardSnapshots };
}

/**
 * @param {Record<string, unknown>} teamDocData
 * @param {Array<{ id: string, snap: import('firebase').firestore.DocumentSnapshot }>} shardSnapshots
 * @param {string} userId
 * @returns {{ type: 'legacy' } | { type: 'shard', shardId: string } | { type: 'none' }}
 */
export function resolveMemberRosterLocation(
  teamDocData,
  shardSnapshots,
  userId
) {
  if (
    teamDocData.memberDetails &&
    teamDocData.memberDetails[userId] !== undefined
  ) {
    return { type: 'legacy' };
  }
  for (const { id, snap } of shardSnapshots) {
    if (
      snap.exists &&
      snap.data()?.[SHARD_MEMBERS_FIELD]?.[userId] !== undefined
    ) {
      return { type: 'shard', shardId: id };
    }
  }
  return { type: 'none' };
}

/**
 * @param {import('firebase').firestore.DocumentReference} teamRef
 * @param {{ type: 'legacy' } | { type: 'shard', shardId: string } | { type: 'none' }} location
 * @param {string} userId
 * @param {Partial<{ userName: string, userType: string, timeZone: string }>} fields
 * @returns {{ ref: import('firebase').firestore.DocumentReference, data: Record<string, unknown> }}
 */
export function buildRosterPatchWrite(teamRef, location, userId, fields) {
  if (location.type === 'none') {
    throw new Error('Member not found on team roster');
  }
  const data = {};
  const keys = /** @type {const} */ (['userName', 'userType', 'timeZone']);
  for (const k of keys) {
    if (fields[k] !== undefined) {
      if (location.type === 'legacy') {
        data[`memberDetails.${userId}.${k}`] = fields[k];
      } else {
        data[`${SHARD_MEMBERS_FIELD}.${userId}.${k}`] = fields[k];
      }
    }
  }
  if (Object.keys(data).length === 0) {
    throw new Error('buildRosterPatchWrite: no fields to update');
  }
  if (location.type === 'legacy') {
    return { ref: teamRef, data };
  }
  const shardRef = teamRef.collection(SHARDS_COLLECTION).doc(location.shardId);
  return { ref: shardRef, data };
}
